/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';

import React, { Component } from 'react';
import { URL } from 'src/constants/links';
import { Banner, Button, Icon, TextStyle } from '@shopify/polaris';
import { CircleTickMajor, ExternalMinor, MobileCancelMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import SendRequestReport from './sendRequestReport';
import RequestSuccessModal from './requestSuccessModal';
import loadingIcon from 'src/assets/images/dashboard-v3/loading-icon.png';
import { connect } from 'react-redux';

import history from 'src/routes/history';
import { routes } from 'src/routes';
import { getShop, getThemeReviewUrl } from 'src/helpers';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';

import * as dashboardV3Actions from 'actions/dashboard-v3-actions';
import { checkUserV3 } from 'src/helpers/subscription-v3-helper';

type State = {
  activeSendRequestModal: boolean,
  activeSuccessModal: boolean,
  showInstallSuccessBanner: boolean,
  showInstallThemeFailBanner: boolean,
  requestModalThemeFailed: boolean
};

class BannerInstallStatus extends Component<WithI18nProps, State> {
  state = {
    requestModalThemeFailed: false,
    activeSendRequestModal: false,
    activeSuccessModal: false,
    showInstallSuccessBanner: true,
    showInstallThemeFailBanner: true
  };

  componentDidMount() {
    const { showInstallationSuccessBanner, showInstallationThemeFailBanner } = this.props;

    if (!showInstallationSuccessBanner) this.setState({ showInstallSuccessBanner: false });
    if (!showInstallationThemeFailBanner) this.setState({ showInstallThemeFailBanner: false });
  }

  render() {
    const {
      requestModalThemeFailed,
      activeSendRequestModal,
      activeSuccessModal,
      showInstallSuccessBanner,
      showInstallThemeFailBanner
    } = this.state;
    const {
      i18n,
      host,
      syncStatus,
      themeName,
      installThemeId,
      isLiveTheme,
      installFeatures,
      themeInstallStatus,
      shopifyThemeName,
      isNewUser,
      tenantApp
    } = this.props;

    if (!showInstallSuccessBanner || !isNewUser || (!syncStatus && !themeInstallStatus)) return '';

    const featuresArr = Object.keys(installFeatures).filter(
      feature =>
        typeof installFeatures[feature] === 'string' && installFeatures[feature] === 'installed'
    );

    const featuresArrTextLabel = featuresArr
      .map(feature => {
        const featureLable = {
          filterCollection: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_filterCollection`
          ),
          filterSearch: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_filterSearch`
          ),
          instantSearch: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_instantSearch`
          ),
          recommendation: i18n.translate(
            `AdminHTML.DashboardV3.ThemeSetupStatus.feature_recommendation`
          )
        };

        return featureLable[feature];
      })
      .join(', ')
      .replace(/,(?=[^,]+$)/, ' and');

    const liveTheme = isLiveTheme ? ` ${i18n.translate('AdminHTML.DashboardV3.Common.live')}` : '';
    const themeInfo = `${themeName || shopifyThemeName}`;

    const doneStatus = syncStatus === 'done' && themeInstallStatus === 'done';
    const themeFailStatus =
      syncStatus === 'done' && themeInstallStatus !== 'done' && themeInstallStatus !== 'running';
    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

    return (
      <>
        {themeFailStatus ? (
          showInstallThemeFailBanner && !isEnableShopifyIntegration ? (
            <Banner
              title={i18n.translate(`AdminHTML.DashboardV3.InstallStatusBanner.labelThemeFail`)}
              action={{
                content: i18n.translate(`AdminHTML.DashboardV3.InstallStatusBanner.buttonText03`),
                onAction: () => {
                  this.setupNewTheme();
                }
              }}
              secondaryAction={{
                content: i18n.translate(`AdminHTML.DashboardV3.InstallStatusBanner.buttonText02`),
                onAction: () => this.toggleModal('activeSendRequestModal', true)
              }}
              status="info"
              onDismiss={() => this.handleCloseThemeFailBanner()}>
              <p>
                {i18n.translate(`AdminHTML.DashboardV3.InstallStatusBanner.contentThemeFail01`)}
              </p>
              <br />
              <p>
                {i18n.translate(`AdminHTML.DashboardV3.InstallStatusBanner.contentThemeFail02`)}
              </p>
            </Banner>
          ) : (
            <></>
          )
        ) : (
          <div className="BC-InstallationStatus__Dashboard">
            {/* Banner success */}
            <div className="icon-status">
              {!doneStatus ? (
                <img src={loadingIcon} alt="loadingIcon" width={18} height={18} />
              ) : (
                <Icon source={CircleTickMajor} color="success" />
              )}
            </div>

            <div className="info-status">
              <div>
                {i18n.translate(
                  `AdminHTML.DashboardV3.InstallStatusBanner.${
                    !doneStatus ? 'labelProcessing' : 'labelSuccess'
                  }`
                )}
                {doneStatus && (
                  <Button onClick={this.handleCloseSuccessBanner} icon={MobileCancelMajor} plain />
                )}
              </div>
              {!doneStatus ? (
                <div className="content content-processing">
                  <p>
                    {i18n.translate(
                      'AdminHTML.DashboardV3.InstallStatusBanner.contentProcessing01',
                      {
                        t1: (
                          <TextStyle variation="strong">
                            {i18n.translate(
                              'AdminHTML.DashboardV3.InstallStatusBanner.contentProcessing01_t1'
                            )}
                          </TextStyle>
                        )
                      }
                    )}
                  </p>
                  <p>
                    {i18n.translate(
                      'AdminHTML.DashboardV3.InstallStatusBanner.contentProcessing02'
                    )}
                  </p>
                </div>
              ) : (
                <div className="content content-success">
                  <p>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess01', {
                      t1: <TextStyle variation="strong">{featuresArrTextLabel}</TextStyle>,
                      t2: liveTheme,
                      t3: <TextStyle variation="strong">{themeInfo}</TextStyle>
                    })}
                  </p>
                  <p>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess02', {
                      t1: (
                        <a
                          href={`${routes.themeSetting.visualEditor(
                            installThemeId
                          )}?shop=${getShop()}&host=${host}`}
                          target="_blank"
                          rel="noopener noreferrer">
                          {i18n.translate(
                            'AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess02_t1'
                          )}
                          <Icon source={ExternalMinor} />
                        </a>
                      )
                    })}
                  </p>
                  <p>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess03', {
                      t1: (
                        <a href={URL['ThemeSetupDoc']} target="_blank" rel="noopener noreferrer">
                          {i18n.translate(
                            'AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess03_t1'
                          )}
                          <Icon source={ExternalMinor} />
                        </a>
                      )
                    })}
                  </p>
                </div>
              )}
              <div className="action-list">
                {!doneStatus ? (
                  <Button disabled>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.buttonText01')}
                  </Button>
                ) : (
                  <Button primary onClick={() => this.handlePreviewClick()}>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.buttonText01')}
                  </Button>
                )}
                <Button onClick={() => this.toggleModal('activeSendRequestModal')}>
                  {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.buttonText02')}
                </Button>
                {!doneStatus && (
                  <p>
                    {i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.subAction', {
                      t1: (
                        <a href={URL['ThemeSetupDoc']} target="_blank" rel="noopener noreferrer">
                          {i18n.translate(
                            'AdminHTML.DashboardV3.InstallStatusBanner.contentSuccess03_t1'
                          )}
                          <Icon source={ExternalMinor} />
                        </a>
                      )
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        <SendRequestReport
          activeSendRequestModal={activeSendRequestModal}
          requestModalThemeFailed={requestModalThemeFailed}
          handleChangeModal={() => this.toggleModal('activeSendRequestModal')}
          handleAfterSend={() => this.toggleModal('activeSuccessModal')}
        />
        <RequestSuccessModal
          active={activeSuccessModal}
          handleChangeModal={() => this.toggleModal('activeSuccessModal')}
        />
      </>
    );
  }

  toggleModal = (fieldName, fromThemeFailed = false) => {
    if (fromThemeFailed) this.setState({ requestModalThemeFailed: true });
    else this.setState({ requestModalThemeFailed: false });

    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  handleCloseThemeFailBanner = () => {
    const { setShowInstallationStatusBanner } = this.props;
    this.setState({ showInstallThemeFailBanner: false });
    setShowInstallationStatusBanner({ enable: false, type: 'themeFailBanner' });
  };

  handleCloseSuccessBanner = () => {
    const { setShowInstallationStatusBanner } = this.props;

    this.setState({ showInstallSuccessBanner: false });
    setShowInstallationStatusBanner({ enable: false, type: 'successBanner' });
  };

  setupNewTheme = () => {
    history.push(routes.themeSetup.view(), { from: 'install-banner' });
  };

  handlePreviewClick = () => {
    const { installThemeId } = this.props;
    window.open(getThemeReviewUrl(installThemeId), '_blank');
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const syncStatus = _.get(state.install, 'syncStatus');
  const themeName = _.get(state.install, 'installThemeName');
  const installThemeId = _.get(state.install, 'installThemeId');
  const isLiveTheme = _.get(state.install, 'themeSetupInfoIsLiveTheme');
  const installFeatures = _.get(state.install, 'installFeatures');
  const themeInstallStatus = _.get(state.install, 'installStatus');
  const shopifyThemeName = _.get(state.install, 'installShopifyThemeName');
  const recentInstalledAt = _.get(tenantApp, 'recentInstalledAt', 0);
  const showInstallationSuccessBanner = _.get(
    tenantApp,
    'settings.onboardingV3.settings.showInstallationSuccessBanner',
    false
  );
  const showInstallationThemeFailBanner = _.get(
    tenantApp,
    'settings.onboardingV3.settings.showInstallationThemeFailBanner',
    false
  );

  return {
    tenantApp,
    tenantId: _.get(tenantApp, 'tenantId', ''),
    host: _.get(state.oauth, 'host', ''),
    syncStatus,
    themeName,
    installThemeId,
    isLiveTheme,
    installFeatures,
    themeInstallStatus,
    shopifyThemeName,
    isNewUser: checkUserV3(recentInstalledAt),
    showInstallationSuccessBanner,
    showInstallationThemeFailBanner
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  setShowInstallationStatusBanner: enable =>
    dispatch(dashboardV3Actions.setShowInstallationStatusBanner(enable, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(BannerInstallStatus));
